import { Component } from 'preact';
import { motion } from 'framer-motion';
import CardInspector, { RARITY_LABEL_COLOR } from './CardInspector';
import CardOwnedBadge from './CardOwnedBadge';
import PackRevealCard from './PackRevealCard';
import PackOpeningFX from './PackOpeningFX';
import { getLocalApiOrigin } from '../utils/localApi';
import { isFoilFinish, resolvePrinting } from '../utils/sorcery/foil.js';
import { playUI, UI } from '../utils/arena/uiSounds';
import { InspectorHoldTracker } from '../utils/inspectorHold';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { sendDraftPick, skipDraft, subscribeToDraftEvents, requestDraftPack } from '../utils/arena/draftApi';
import { isDevBuild } from '../utils/devMode';
import { resolvePackContents } from '../utils/arena/packsApi';
import { buildOwnedMap } from '../utils/arena/collectionUtils';
import { isFreePlay } from '../utils/freePlay';
import { getMaxCopies } from '../utils/sorcery/deckRules';
import DeckEditorSidebar from './DeckEditorSidebar';
import { ART_GROUP_BASE, artGroupOf } from '../utils/sorcery/artGroups';
import DraftFooter from './DraftFooter';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD, PANEL_BG,
  BEVELED_BTN, GOLD_BTN, RADIUS,
  OrnamentalDivider,
} from '../lib/medievalTheme';
import { t } from '../localization/t';
import RuneSpinner from './RuneSpinner';

const RARITY_GLOW = {
  Ordinary: '0 0 8px rgba(255,255,255,0.06)',
  Exceptional: '0 0 18px rgba(59,130,246,0.4), 0 0 36px rgba(59,130,246,0.12)',
  Elite: '0 0 22px rgba(168,85,247,0.5), 0 0 44px rgba(168,85,247,0.18)',
  Unique: '0 0 28px rgba(245,158,11,0.6), 0 0 56px rgba(245,158,11,0.22), 0 0 80px rgba(245,158,11,0.1)',
  Avatar: '0 0 30px rgba(220,40,40,0.5), 0 0 60px rgba(220,40,40,0.2), 0 0 90px rgba(245,158,11,0.1)',
};

const RARITY_GLOW_HOVER = {
  Ordinary: '0 0 18px rgba(255,255,255,0.12), 0 0 36px rgba(255,255,255,0.04)',
  Exceptional: '0 0 28px rgba(59,130,246,0.55), 0 0 56px rgba(59,130,246,0.2)',
  Elite: '0 0 32px rgba(168,85,247,0.65), 0 0 64px rgba(168,85,247,0.25)',
  Unique: '0 0 38px rgba(245,158,11,0.75), 0 0 76px rgba(245,158,11,0.3), 0 0 110px rgba(245,158,11,0.12)',
  Avatar: '0 0 40px rgba(220,40,40,0.7), 0 0 80px rgba(220,40,40,0.3), 0 0 120px rgba(245,158,11,0.15)',
};

const RARITY_COLORS = {
  Unique: '#e8c840', Elite: '#c860e0', Exceptional: '#4898e0', Ordinary: TEXT_BODY,
};

const BOOSTER_SCALE = { gothic: 1, arthurian: 1.4, beta: 1 };

function getBoosterImage(setKey) {
  const base = getLocalApiOrigin();
  return `${base}/game-assets/booster-${setKey}.webp`;
}

function playSound(src, volume = 0.5) {
  playUI(src, { volume });
}

/**
 * True iff every key in `subset` has a count ≤ its count in `superset`.
 * Both arguments are Map<key, count> multisets.
 */
function isSubMultiset(subset, superset) {
  for (const [key, count] of subset) {
    if ((superset.get(key) || 0) < count) return false;
  }
  return true;
}

export default class DraftPicker extends Component {
  constructor(props) {
    super(props);
    this.state = {
      rawPack: [],
      // 'standard' (pack-and-pass pod) or 'sealed' (solo pool).
      // Server sends this on the pack envelope; default until first
      // pack arrives keeps the legacy pod semantics for everything
      // already in flight.
      mode: 'standard',
      totalPacks: 3,
      picks: [],
      chosenCards: [],
      packNumber: 1,
      pickNumber: 1,
      direction: 'left',
      timeLeft: 90,
      // Pack opening states
      phase: 'waiting', // 'waiting' | 'sealed' | 'opening' | 'cards'
      entryDone: false,
      // Card interaction
      selectedIndex: null,
      hoveredIndex: -1,
      inspectedEntry: null,
      // Pick state
      confirming: false,
      waitingForOthers: false,
      // Footer intel: every card occurrence we've ever witnessed in a
      // distinct physical pack (duplicates included. If a pack has
      // 2× Fire Bolt, both copies count). Dedup is by *pack identity*,
      // not card identity: a pack rotating back to us shows a strict
      // sub-multiset of the cards we saw on its first visit, so
      // sub-multiset detection collapses rotations onto the original
      // visit. Cross-pack signatures are scoped by packNumber so two
      // distinct packs across different rounds can't accidentally
      // collide. Pack count reflects the number of distinct physical
      // packs we've witnessed (caps at players × rounds. E.g.
      // 2-player × 3 packs = 6).
      seenEntries: [],
      seenPackSignatures: [],
      uniquePackCount: 0,
    };
    this.timer = null;
    this.inspectorHold = new InspectorHoldTracker();
  }

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    // Resume safety net: if we mounted into an in-progress draft (e.g. after
    // an app reload) the original pack broadcast already fired and was
    // dropped, leaving us stuck on "Waiting for packs..." If no pack has
    // arrived shortly, ask the server to re-send our current one. Fresh draft
    // starts deliver the pack well within this window, so the request only
    // fires on a genuine resume.
    this._packRequestTimer = setTimeout(() => {
      if (this.state.phase === 'waiting') requestDraftPack();
    }, 1000);
    this.unsubDraft = subscribeToDraftEvents({
      pack: (data) => {
        // Ignore pack events from a different draft (stale timers from old drafts)
        if (data.eventId && this.props.eventId && data.eventId !== this.props.eventId) return;
        clearTimeout(this._packRequestTimer);

        const isNewPack = data.packNumber !== this.state.packNumber;
        const isFirstPack = this.state.phase === 'waiting';

        // Pack-and-pass: a pack rotates through us multiple times per
        // round. Each rotation is a strict sub-multiset of the cards
        // we saw on its first visit (some have been picked since).
        // Sub-multiset detection (scoped to the same packNumber)
        // collapses every rotation onto the first visit so the count
        // only ticks when a *new physical pack* arrives.
        const newSeen = this.appendSeenPack(data.cards || [], data.packNumber);

        this.setState({
          rawPack: data.cards || [],
          packNumber: data.packNumber,
          pickNumber: data.pickNumber,
          direction: data.direction,
          mode: data.mode || this.state.mode || 'standard',
          totalPacks: data.totalPacks || (data.mode === 'sealed' ? 6 : 3),
          timeLeft: 90,
          selectedIndex: null,
          confirming: false,
          waitingForOthers: false,
          entryDone: false,
          inspectedEntry: null,
          // Only show pack opening animation for genuinely new packs
          phase: (isNewPack || isFirstPack) ? 'sealed' : 'cards',
          seenEntries: newSeen.entries,
          seenPackSignatures: newSeen.signatures,
          uniquePackCount: newSeen.uniquePackCount,
        });

        // If skipping straight to cards (rotation within same pack), trigger entry done quickly
        if (!isNewPack && !isFirstPack) {
          setTimeout(() => this.setState({ entryDone: true }), 100);
        }
      },
      pick_confirmed: (data) => {
        this.setState((s) => {
          // Sealed: remove the picked card from rawPack locally and
          // stay in the picking phase. The server doesn't send a
          // fresh pack for each pick within the same 15-card pack.
          // Standard: server re-broadcasts the rotated pack next
          // round, so we can safely go into "waiting for others".
          const isSealed = s.mode === 'sealed';
          const picks = [...s.picks, data.card];
          const chosenCards = this.addPickToChosen(s.chosenCards, data.card);
          if (isSealed) {
            const nextPack = s.rawPack.filter(
              (c) => !(c.cardId === data.card.cardId && c.printingId === data.card.printingId)
            );
            return {
              picks,
              chosenCards,
              confirming: false,
              waitingForOthers: false,
              selectedIndex: null,
              rawPack: nextPack,
              pickNumber: (s.pickNumber || 0) + 1,
            };
          }
          return {
            picks,
            chosenCards,
            confirming: false,
            waitingForOthers: true,
          };
        });
        playUI(UI.CARD_PLACE);
      },
      my_picks: (data) => {
        if (data.eventId && this.props.eventId && data.eventId !== this.props.eventId) return;
        // Resume: rebuild the drafted-pool sidebar from the server's
        // authoritative picks. Skip if we already have picks this session so
        // a late/duplicate snapshot can't clobber live state.
        this.setState((s) => {
          if (s.picks.length) return null;
          const incoming = data.picks || [];
          let chosenCards = s.chosenCards;
          for (const card of incoming) chosenCards = this.addPickToChosen(chosenCards, card);
          return { picks: [...incoming], chosenCards };
        });
      },
      auto_picked: (data) => {
        this.setState((s) => ({
          picks: [...s.picks, data.card],
          chosenCards: this.addPickToChosen(s.chosenCards, data.card),
          confirming: false,
          selectedIndex: null,
          waitingForOthers: true,
        }));
      },
      pack_complete: (data) => {
        if (data.eventId && this.props.eventId && data.eventId !== this.props.eventId) return;
        this.setState({
          packNumber: data.nextPackNumber,
          direction: data.nextDirection,
        });
      },
      building: (data) => {
        if (data.eventId && this.props.eventId && data.eventId !== this.props.eventId) return;
        this.props.onBuildingPhase?.(data);
      },
      timer: (data) => {
        if (data.eventId && this.props.eventId && data.eventId !== this.props.eventId) return;
        this.setState({ timeLeft: data.timeLeft });
      },
    });

    if (this.props.initialPack) {
      const init = this.props.initialPack;
      const seeded = this.appendSeenPack(init.cards || [], init.packNumber || 1);
      this.setState({
        rawPack: init.cards || [],
        packNumber: init.packNumber || 1,
        pickNumber: init.pickNumber || 1,
        direction: init.direction || 'left',
        phase: 'sealed',
        seenEntries: seeded.entries,
        seenPackSignatures: seeded.signatures,
        uniquePackCount: seeded.uniquePackCount,
      });
    }

    this.timer = setInterval(() => {
      this.setState((s) => ({ timeLeft: Math.max(0, s.timeLeft - 1) }));
    }, 1000);
  }

  componentDidUpdate(_prevProps, prevState) {
    // Space-was-armed → hover landed on a card → open inspector now.
    if (
      !this.state.inspectedEntry &&
      this.state.phase === 'cards' &&
      this.state.hoveredIndex >= 0 &&
      this.state.hoveredIndex !== prevState.hoveredIndex &&
      this.inspectorHold.consumePending()
    ) {
      const resolved = this.resolvedPack();
      const entry = resolved[this.state.hoveredIndex];
      if (entry) {
        playUI(UI.INSPECTOR_OPEN);
        this.setState({ inspectedEntry: entry });
      }
    }

    const inspectorOpen = this.state.inspectedEntry != null;
    const wasInspectorOpen = prevState.inspectedEntry != null;
    if (inspectorOpen && !wasInspectorOpen) {
      this._escDismissId = pushDismiss(() => {
        playUI(UI.INSPECTOR_CLOSE);
        this.setState({ inspectedEntry: null });
      });
    } else if (!inspectorOpen && wasInspectorOpen) {
      removeDismiss(this._escDismissId);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    removeDismiss(this._escDismissId);
    this.unsubDraft?.();
    clearInterval(this.timer);
    clearTimeout(this._packRequestTimer);
    if (this._previewRafId) cancelAnimationFrame(this._previewRafId);
  }

  handleKeyDown = (e) => {
    if (e.repeat) return;
    const tag = e.target?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;

    if (e.key === ' ' || e.code === 'Space') {
      e.preventDefault();
      if (this.state.inspectedEntry) {
        playUI(UI.INSPECTOR_CLOSE);
        this.inspectorHold.noteClose();
        this.setState({ inspectedEntry: null });
      } else if (this.state.hoveredIndex >= 0 && this.state.phase === 'cards') {
        const resolved = this.resolvedPack();
        if (resolved[this.state.hoveredIndex]) {
          playUI(UI.INSPECTOR_OPEN);
          this.inspectorHold.noteOpen();
          this.setState({ inspectedEntry: resolved[this.state.hoveredIndex] });
        }
      } else if (this.state.phase === 'cards') {
        // No card hovered yet. Arm so the next hover opens.
        this.inspectorHold.noteArmed();
      }
    }
  };

  // Releasing Space closes the inspector only if it was held past the
  // hold threshold; quick taps leave it open (tap-toggle mode).
  handleKeyUp = (e) => {
    if (e.key !== ' ' && e.code !== 'Space') return;
    // Tracker first: releasing Space also disarms a hover-less press.
    if (!this.inspectorHold.shouldCloseOnKeyUp() || !this.state.inspectedEntry) return;
    e.preventDefault();
    playUI(UI.INSPECTOR_CLOSE);
    this.setState({ inspectedEntry: null });
  };

  resolvedPack() {
    return resolvePackContents(this.state.rawPack, this.props.sorceryCards || []);
  }

  /**
   * Merge a freshly-arrived pack into the running draft-pool intel.
   *
   * Each physical pack gets logged exactly once, but every card
   * occurrence inside that pack. Duplicates included. Appears in
   * `entries`. Detection works on multisets: the cards present in any
   * later rotation of a pack are a strict sub-multiset of what we saw
   * on its first visit (cards only leave a pack as picks are taken).
   * So if the incoming multiset fits inside any signature we've
   * already recorded for the same `packNumber`, this is a rotation.
   * skip. Otherwise it's a new physical pack: store its signature and
   * append all its cards as entries.
   *
   * Scoping the match by `packNumber` prevents cross-round false
   * positives. A round-2 pack can never look like a sub-multiset of
   * a round-1 pack we've already tracked.
   */
  appendSeenPack(rawCards, packNumber) {
    const prevEntries = this.state.seenEntries;
    const prevSignatures = this.state.seenPackSignatures;
    const prevPackCount = this.state.uniquePackCount;

    const resolved = resolvePackContents(rawCards || [], this.props.sorceryCards || []);
    if (resolved.length === 0) {
      return { entries: prevEntries, signatures: prevSignatures, uniquePackCount: prevPackCount };
    }

    const candidateMultiset = new Map();
    const candidateEntries = [];
    for (const r of resolved) {
      const card = r?.card;
      if (!card) continue;
      const printingId = r.printing?.unique_id || r.printingId || '';
      const key = `${card.unique_id}::${printingId}`;
      candidateMultiset.set(key, (candidateMultiset.get(key) || 0) + 1);
      const isSite = card.type === 'Site' || card._sorceryCategory === 'Site' || card.played_horizontally;
      const isAvatar = card.type === 'Avatar' || card._sorceryCategory === 'Avatar';
      const zone = isAvatar ? 'avatar' : isSite ? 'atlas' : 'spellbook';
      candidateEntries.push({ card, printing: r.printing, zone });
    }

    if (candidateEntries.length === 0) {
      return { entries: prevEntries, signatures: prevSignatures, uniquePackCount: prevPackCount };
    }

    for (const sig of prevSignatures) {
      if (sig.packNumber !== packNumber) continue;
      if (isSubMultiset(candidateMultiset, sig.multiset)) {
        // Rotation of a pack we already counted. Same cards minus
        // whatever's been picked since.
        return { entries: prevEntries, signatures: prevSignatures, uniquePackCount: prevPackCount };
      }
    }

    return {
      entries: [...prevEntries, ...candidateEntries],
      signatures: [...prevSignatures, { packNumber, multiset: candidateMultiset }],
      uniquePackCount: prevPackCount + 1,
    };
  }

  addPickToChosen(chosenCards, rawCard) {
    const sorceryCards = this.props.sorceryCards || [];
    const card = sorceryCards.find((c) => c.unique_id === rawCard.cardId);
    if (!card) return chosenCards;
    const printing = resolvePrinting(card, rawCard.printingId, rawCard.foiling || 'S') || {};
    const isSite = card.type === 'Site' || card._sorceryCategory === 'Site' || card.played_horizontally;
    const isAvatar = card.type === 'Avatar' || card._sorceryCategory === 'Avatar';
    const zone = isAvatar ? 'avatar' : isSite ? 'atlas' : 'spellbook';
    return [...chosenCards, { card, printing, zone, uniqueId: card.unique_id, foiling: printing.foiling || 'S' }];
  }

  // --- Sidebar hover preview (same RAF system as DeckEditor) ---
  _previewAnim = { opacity: 0, top: 0, targetTop: 0, visible: false, imgUrl: '', cardName: '', isSite: false, isFoil: false, foiling: '', sidebarLeft: 0 };
  _previewRafId = null;

  handleSidebarHover = (cardUniqueId, hoverArtGroup, hoverFoiling, rowElement) => {
    if (!cardUniqueId) {
      this._previewAnim.visible = false;
      this._startPreviewLoop();
      return;
    }
    const targetGroup = hoverArtGroup || ART_GROUP_BASE;
    const targetFoiling = hoverFoiling || 'S';
    const entry = this.state.chosenCards.find((c) =>
      c.card?.unique_id === cardUniqueId
      && artGroupOf(c.printing) === targetGroup
      && (c.printing?.foiling || 'S') === targetFoiling
    ) || this.state.chosenCards.find((c) => c.card?.unique_id === cardUniqueId);
    if (!entry) return;
    // Prefer the exact hovered element. The same card can appear in multiple
    // zones, and querySelector would otherwise pick the first DOM match.
    const row = rowElement
      || document.querySelector(`[data-sidebar-card-id="${cardUniqueId}"][data-sidebar-art-group="${targetGroup}"][data-sidebar-foiling="${targetFoiling}"]`)
      || document.querySelector(`[data-sidebar-card-id="${cardUniqueId}"]`);
    const rect = row?.getBoundingClientRect();
    if (!rect) return;

    // Canvas-local coords: preview is position: fixed inside .app-canvas.
    const canvasEl = document.querySelector('.app-canvas');
    const canvasRect = canvasEl ? canvasEl.getBoundingClientRect() : { left: 0, top: 0, height: window.innerHeight };
    const canvasH = canvasRect.height;
    const localCenterY = (rect.top - canvasRect.top) + rect.height / 2;
    const localLeft = rect.left - canvasRect.left;

    const imgUrl = entry.printing?.image_url || entry.card?.printings?.[0]?.image_url || '';
    const isSite = entry.card?.played_horizontally;
    const foiling = entry.printing?.foiling || 'S';
    const isFoil = foiling === 'F' || foiling === 'R';
    const previewW = isSite ? 420 : 300;
    const previewH = isSite ? 300 : 420;
    let targetTop = localCenterY - previewH / 2;
    if (targetTop < 8) targetTop = 8;
    if (targetTop + previewH > canvasH - 8) targetTop = canvasH - 8 - previewH;

    const anim = this._previewAnim;
    const wasVisible = anim.visible;
    anim.visible = true; anim.imgUrl = imgUrl; anim.cardName = entry.card?.name || '';
    anim.isSite = isSite; anim.isFoil = isFoil; anim.foiling = foiling;
    anim.sidebarLeft = localLeft; anim.targetTop = targetTop; anim.previewW = previewW; anim.previewH = previewH;
    if (!wasVisible) { anim.top = targetTop; anim.opacity = 0; }
    this._startPreviewLoop();
  };

  _startPreviewLoop = () => { if (!this._previewRafId) this._previewRafId = requestAnimationFrame(this._tickPreview); };

  _tickPreview = () => {
    this._previewRafId = null;
    const anim = this._previewAnim;
    const speed = 0.18;
    if (anim.visible) { anim.opacity += (1 - anim.opacity) * speed; if (anim.opacity > 0.99) anim.opacity = 1; }
    else { anim.opacity += (0 - anim.opacity) * (speed * 1.5); if (anim.opacity < 0.01) anim.opacity = 0; }
    anim.top += (anim.targetTop - anim.top) * speed;

    const el = this._previewRef;
    if (el) {
      const { previewW = 300, previewH = 420 } = anim;
      const left = anim.sidebarLeft - previewW - 16;
      el.style.opacity = String(anim.opacity); el.style.top = `${anim.top}px`; el.style.left = `${left}px`;
      el.style.width = `${previewW}px`; el.style.height = `${previewH}px`;
      el.style.transform = `scale(${0.85 + anim.opacity * 0.15})`; el.style.display = anim.opacity > 0.01 ? 'block' : 'none';
      const inner = el.querySelector('[data-preview-inner]');
      if (inner) {
        if (anim.isFoil) { inner.className = 'w-full h-full rounded-card card-mask foil-overlay foil-overlay--always'; inner.setAttribute('data-foil', anim.foiling); }
        else { inner.className = 'w-full h-full rounded-card card-mask'; inner.removeAttribute('data-foil'); }
      }
      const img = el.querySelector('img');
      if (img && img.src !== anim.imgUrl && anim.imgUrl) img.src = anim.imgUrl;
      if (img) {
        img.alt = anim.cardName;
        if (anim.isSite) { img.style.transform = 'rotate(90deg)'; img.style.transformOrigin = 'center center'; img.style.width = `${previewH}px`; img.style.height = `${previewW}px`; img.style.position = 'absolute'; img.style.top = `${(previewH - previewW) / 2}px`; img.style.left = `${(previewW - previewH) / 2}px`; }
        else { img.style.transform = 'none'; img.style.width = '100%'; img.style.height = '100%'; img.style.position = 'static'; img.style.top = ''; img.style.left = ''; }
      }
    }
    const isSettled = anim.visible ? anim.opacity >= 1 && Math.abs(anim.top - anim.targetTop) < 0.5 : anim.opacity <= 0;
    if (!isSettled) this._previewRafId = requestAnimationFrame(this._tickPreview);
  };

  handlePackClick = () => {
    if (this.state.phase !== 'sealed') return;
    playSound('snd-pack-opening.mp3', 0.6);
    this.setState({ phase: 'opening' });
    setTimeout(() => {
      playSound('snd-card-slide-1.ogg', 0.4);
      this.setState({ phase: 'cards', entryDone: false });
      const cardCount = this.state.rawPack.length || 15;
      setTimeout(() => this.setState({ entryDone: true }), cardCount * 50 + 400);
    }, 900);
  };

  handleSelectCard = (index) => {
    if (this.state.confirming || this.state.waitingForOthers) return;
    this.setState({ selectedIndex: index });
    playUI(UI.HOVER, { volume: 0.5 });
  };

  handleConfirmPick = () => {
    const { selectedIndex, rawPack } = this.state;
    if (selectedIndex === null || this.state.confirming || this.state.waitingForOthers) return;
    const raw = rawPack[selectedIndex];
    if (!raw) return;
    this.setState({ confirming: true });
    sendDraftPick(raw.cardId, raw.printingId);
  };

  handleSkipDraft = async () => {
    try {
      await skipDraft(this.props.eventId);
    } catch (err) {
      console.error('[DraftPicker] skip failed:', err);
    }
  };

  // Hostname-based dev detection leaks into packaged builds (the
  // production renderer ALSO runs on 127.0.0.1). Use the shared
  // URL-param check instead so dev-only UI can never ship.
  isDevMode() {
    return isDevBuild();
  }

  render() {
    const { sorceryCards } = this.props;
    const { packNumber, pickNumber, direction, timeLeft, phase, entryDone, selectedIndex, hoveredIndex, inspectedEntry, confirming, waitingForOthers, picks, rawPack, mode, totalPacks } = this.state;

    const resolved = this.resolvedPack();
    const ownedMap = buildOwnedMap(this.props.profile?.collection || []);
    const selectedEntry = selectedIndex !== null ? resolved[selectedIndex] : null;
    const timerUrgent = timeLeft <= 15;
    const setKey = rawPack[0]?.setKey || this.props.setKey || 'beta';
    const isSealed = mode === 'sealed';
    const totalPacksDisplay = totalPacks || (isSealed ? 6 : 3);

    return (
      <div className="fixed inset-0 z-50 bg-black flex flex-col overflow-hidden select-none">
        <PackOpeningFX active={phase === 'opening' || phase === 'cards'} />

        {/* Header bar */}
        <div className="flex items-center gap-4 px-6 py-3 relative z-10" style={{ background: PANEL_BG, borderBottom: `1px solid ${GOLD} 0.15)`, backdropFilter: 'blur(8px)' }}>
          <div className="flex items-center gap-4 flex-1">
            <div className="text-sm font-semibold arena-heading" style={{ color: ACCENT_GOLD }}>{t('Pack {n}/{total}', { n: packNumber, total: totalPacksDisplay })}</div>
            <div className="text-sm" style={{ color: TEXT_BODY }}>{t('Pick {n}/15', { n: pickNumber })}</div>
            {isSealed ? (
              <div className="text-xs px-2 py-0.5 rounded" style={{ color: TEXT_MUTED, background: 'rgba(255,255,255,0.05)' }}>
                {t('Sealed. No passing')}
              </div>
            ) : (
              <div className="text-xs px-2 py-0.5 rounded" style={{ color: TEXT_MUTED, background: 'rgba(255,255,255,0.05)' }}>
                {direction === 'left' ? t('Pass ← left') : t('Pass → right')}
              </div>
            )}
          </div>
          {phase === 'cards' ? <div className="text-xs mr-3" style={{ color: TEXT_MUTED }}>{t('Space to inspect')}</div> : null}
          {this.isDevMode() ? (
            <button
              type="button"
              className="px-3 py-1 text-[0.625cqw] font-bold transition-all hover:scale-[1.05] active:scale-[0.97] mr-2"
              style={{
                background: 'linear-gradient(180deg, rgba(180,60,60,0.15) 0%, rgba(120,30,30,0.1) 100%)',
                border: '1px solid rgba(180,60,60,0.35)',
                borderRadius: 'var(--radius-sm)',
                color: '#c45050',
              }}
              onClick={this.handleSkipDraft}
            >
              DEV: Skip Draft
            </button>
          ) : null}
          {/* Sealed has no per-pick pressure. Each player opens the
              6-pack pool at their own pace. So the timer UI is
              hidden. Standard draft keeps the 90s auto-pick clock. */}
          {!isSealed ? (
            <div
              className="text-xl font-bold tabular-nums px-4 py-1 rounded"
              style={{
                color: timerUrgent ? '#e06060' : TEXT_PRIMARY,
                background: timerUrgent ? 'rgba(224,96,96,0.1)' : 'rgba(0,0,0,0.3)',
                animation: timerUrgent ? 'pulse 1s infinite' : 'none',
              }}
            >
              {t('{seconds}s', { seconds: timeLeft })}
            </div>
          ) : null}
        </div>

        <div className="flex-1 flex relative z-10 overflow-hidden">
          {/* Main content area.
              `min-w-0` is load-bearing: without it, the 8-card pack
              row (8×150px + gaps ≈ 1200px) sets a min-content floor
              that refuses to shrink, so on narrower viewports the
              flex calc pushes the 300px sidebar past the viewport
              edge and the row's overflow-hidden clips it.
              `overflow-hidden` on the column itself lets the cards
              visually clip symmetrically inside the main area
              instead of bleeding into the sidebar zone. */}
          <div className="flex-1 min-w-0 flex flex-col items-center justify-center overflow-hidden">

            {/* Waiting for pack */}
            {phase === 'waiting' ? (
              <div className="flex flex-col items-center gap-4">
                <RuneSpinner size={64} />
                <div className="text-sm" style={{ color: TEXT_MUTED }}>{t('Waiting for packs...')}</div>
              </div>
            ) : null}

            {/* Sealed pack. Click to open */}
            {(phase === 'sealed' || phase === 'opening') ? (
              <div className="flex flex-col items-center gap-6">
                {phase === 'sealed' ? (
                  <>
                    <h2 className="text-lg font-bold arena-heading" style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                      {t('Pack {pack}. Pick {pick}/15', { pack: packNumber, pick: pickNumber })}
                    </h2>
                    <motion.button
                      type="button"
                      className="cursor-pointer"
                      whileHover={{ scale: 1.08 }}
                      whileTap={{ scale: 0.96 }}
                      onClick={this.handlePackClick}
                    >
                      <div style={{ transform: `scale(${BOOSTER_SCALE[setKey] || 1})` }}>
                        <img
                          src={getBoosterImage(setKey)}
                          alt=""
                          className="max-w-[11.25cqw] max-h-[17.5cqw] object-contain drop-shadow-[0_20px_50px_rgba(0,0,0,0.7)]"
                          draggable={false}
                        />
                      </div>
                    </motion.button>
                    <motion.p
                      className="text-sm"
                      style={{ color: TEXT_MUTED }}
                      animate={{ opacity: [0.4, 0.8, 0.4] }}
                      transition={{ repeat: Infinity, duration: 2 }}
                    >
                      {t('Click pack to reveal cards')}
                    </motion.p>
                  </>
                ) : (
                  <motion.div className="flex flex-col items-center gap-6" initial={{ scale: 1 }} animate={{ scale: 1.1, opacity: 0.9 }} transition={{ duration: 0.3 }}>
                    <motion.div
                      animate={{ x: [0, -8, 8, -6, 6, -4, 4, -2, 2, 0], rotate: [0, -3, 3, -2.5, 2.5, -1.5, 1.5, -0.5, 0.5, 0] }}
                      transition={{ duration: 0.5 }}
                    >
                      <div style={{ transform: `scale(${BOOSTER_SCALE[setKey] || 1})` }}>
                        <img src={getBoosterImage(setKey)} alt="" className="max-w-[15cqw] max-h-[22.5cqw] object-contain drop-shadow-[0_25px_70px_rgba(0,0,0,0.8)]" draggable={false} />
                      </div>
                    </motion.div>
                    <motion.p className="text-sm" style={{ color: ACCENT_GOLD }} animate={{ opacity: [0.5, 1, 0.5] }} transition={{ repeat: Infinity, duration: 0.8 }}>
                      {t('Opening...')}
                    </motion.p>
                  </motion.div>
                )}
              </div>
            ) : null}

            {/* Cards revealed. Pick one */}
            {phase === 'cards' ? (
              <div className="flex flex-col items-center w-full h-full">
                <div
                  className="flex-1 flex flex-col items-center justify-center gap-4 w-full px-8"
                  onMouseLeave={() => this.setState({ hoveredIndex: -1 })}
                >
                  {[resolved.slice(0, 8), resolved.slice(8)].map((row, rowIdx) => {
                    const rowStart = rowIdx === 0 ? 0 : 8;
                    return (
                      <div key={rowIdx} className="relative flex items-end justify-center gap-2" style={{ perspective: '1200px' }}>
                        {row.map((entry, ri) => {
                          const i = rowStart + ri;
                          const rowN = row.length;
                          const rarity = entry.rarity || entry.card?.rarity || 'Ordinary';
                          const entryFoil = isFoilFinish(entry.printing?.foiling);
                          const isHovered = hoveredIndex === i;
                          const isSelected = selectedIndex === i;
                          // Card dimensions in cqw. 150×210 design-px on
                          // the 1600 design canvas = 9.375 × 13.125 cqw.
                          const cardWidth = '9.375cqw';
                          const cardHeight = '13.125cqw';

                          const fanT = rowN === 1 ? 0 : (ri / (rowN - 1)) - 0.5;
                          const fanRotate = fanT * 6;
                          const fanY = `${Math.abs(fanT) * 0.75}cqw`;

                          return (
                            <PackRevealCard
                              key={i}
                              index={i}
                              entryInitialRotate={(Math.random() - 0.5) * 30}
                              animateOpacity={waitingForOthers ? 0.35 : 1}
                              animateY={isSelected ? fanY - 12 : fanY}
                              animateScale={isSelected ? 1.06 : 1}
                              fanRotate={fanRotate}
                              zIndex={isHovered ? 100 : i + 1}
                              entryDone={entryDone}
                              entryFoil={entryFoil}
                              cardWidth={cardWidth}
                              cardHeight={cardHeight}
                              isHovered={isHovered}
                              auraClassName={entryFoil ? 'foil-card-aura' : undefined}
                              glowStyle={{
                                position: 'absolute', inset: 0, borderRadius: RADIUS.card,
                                boxShadow: isSelected
                                  ? `0 0 20px rgba(212,168,67,0.5), 0 0 40px rgba(212,168,67,0.2)`
                                  : (entryFoil ? undefined : (isHovered ? RARITY_GLOW_HOVER[rarity] : RARITY_GLOW[rarity])),
                                transition: 'box-shadow 0.2s ease',
                                pointerEvents: 'none',
                              }}
                              cardBoxStyle={{
                                border: isSelected ? `2px solid ${ACCENT_GOLD}` : '2px solid transparent',
                                filter: isHovered && !isSelected ? 'brightness(1.15)' : 'none',
                                transition: 'filter 0.2s ease, border-color 0.15s ease',
                                cursor: waitingForOthers ? 'default' : 'pointer',
                              }}
                              tileEntry={{ card: entry.card, printing: entry.printing || {}, zone: 'spellbook', entryIndex: i }}
                              cornerBadge={isFreePlay() ? null : (
                                <CardOwnedBadge
                                  owned={ownedMap.get(entry.card?.unique_id) || 0}
                                  maxCopies={getMaxCopies(entry.card) || 4}
                                  hideWhenZero
                                />
                              )}
                              onClick={() => this.handleSelectCard(i)}
                              onHoverChange={(hovered) => {
                                this.setState({ hoveredIndex: hovered ? i : -1 });
                                if (hovered) playUI(UI.HOVER, { volume: 0.4 });
                              }}
                              tooltip={{ name: entry.card?.name, rarityLabel: rarity, rarityColorClass: RARITY_LABEL_COLOR[rarity] }}
                            />
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                {/* Bottom action bar */}
                <motion.div
                  className="flex items-center gap-4 pb-6 relative z-10"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  {waitingForOthers ? (
                    <div className="flex items-center gap-3 px-6 py-2.5">
                      <RuneSpinner size={20} />
                      <span className="text-sm" style={{ color: TEXT_MUTED }}>{t('Waiting for other players...')}</span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="px-8 py-2.5 text-sm font-bold transition-all cursor-pointer"
                      style={{
                        ...(selectedEntry ? GOLD_BTN : BEVELED_BTN),
                        borderRadius: 'var(--radius-lg)',
                        opacity: selectedEntry ? 1 : 0.5,
                        color: selectedEntry ? undefined : TEXT_MUTED,
                      }}
                      data-sound={UI.CONFIRM}
                      disabled={!selectedEntry || confirming}
                      onClick={this.handleConfirmPick}
                    >
                      {confirming ? t('Picking...') : selectedEntry ? t('Pick {card}', { card: selectedEntry.card?.name }) : t('Select a card')}
                    </button>
                  )}
                </motion.div>

                <DraftFooter
                  seenEntries={this.state.seenEntries}
                  seenPackCount={this.state.uniquePackCount}
                  chosenCards={this.state.chosenCards}
                />
              </div>
            ) : null}
          </div>

          {/* Right sidebar: DeckEditorSidebar with picks */}
          <div className="w-[18.75cqw] shrink-0 overflow-hidden h-full">
            <DeckEditorSidebar
              chosenCards={this.state.chosenCards}
              draftMode
              onIncrement={() => {}}
              onDecrement={() => {}}
              onChangeZone={() => {}}
              onCardHover={this.handleSidebarHover}
            />
          </div>
        </div>

        {/* Sidebar hover preview */}
        <div
          ref={(el) => { this._previewRef = el; }}
          className="fixed pointer-events-none z-[60]"
          style={{ display: 'none', opacity: 0, borderRadius: RADIUS.card, boxShadow: '0 20px 60px rgba(0,0,0,0.7), 0 0 30px rgba(0,0,0,0.4)', transformOrigin: 'right center', willChange: 'transform, opacity, top' }}
        >
          <div data-preview-inner className="w-full h-full rounded-card card-mask">
            <img src="" alt="" className="object-cover" draggable={false} />
          </div>
        </div>

        {/* Card inspector */}
        {inspectedEntry ? (
          <CardInspector
            card={inspectedEntry.card}
            imageUrl={inspectedEntry.printing?.image_url}
            rarity={inspectedEntry.rarity}
            foiling={inspectedEntry.printing?.foiling}
            onClose={() => { playUI(UI.INSPECTOR_CLOSE); this.setState({ inspectedEntry: null }); }}
          />
        ) : null}
      </div>
    );
  }
}
